/**
 * FacetWP property listing — loading overlay + result enter animation
 * (filters, sort, pager).
 */

const LOADING_CLASS = 'vzp-facetwp-listing--loading';
const ENTER_CLASS = 'vzp-facetwp-listing--enter';
const LOADER_OUT_CLASS = 'vzp-facetwp-listing__loader--out';
const ROOT_SELECTOR = '.vzp-property-listing__results';
const LOADER_SELECTOR = '.vzp-facetwp-listing__loader';
const ITEM_SELECTOR = '.vzp-facetwp-properties__item, .vzp-facetwp-properties-table__row';

const LOADING_LABEL = 'Laden…';
const MIN_LOADING_MS = 320;
const LOADER_OUT_MS = 220;
const STAGGER_MS = 45;
const STAGGER_MAX_MS = 360;

/** @type {number} */
let loadingStartedAt = 0;

/** @type {ReturnType<typeof setTimeout>|null} */
let hideTimer = null;

/** @type {ReturnType<typeof setTimeout>|null} */
let enterCleanupTimer = null;

function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function shouldShowLoader() {
	return Boolean(typeof window !== 'undefined' && window.FWP && window.FWP.loaded);
}

function ensureLoader(root) {
	let loader = root.querySelector(LOADER_SELECTOR);
	if (loader) {
		return loader;
	}

	loader = document.createElement('div');
	loader.className = 'vzp-facetwp-listing__loader';
	loader.hidden = true;
	loader.setAttribute('role', 'status');
	loader.setAttribute('aria-live', 'polite');
	loader.innerHTML =
		'<span class="vzp-facetwp-listing__loader-spinner" aria-hidden="true"></span>' +
		'<span class="vzp-facetwp-listing__loader-dots" aria-hidden="true">' +
			'<span></span><span></span><span></span>' +
		'</span>' +
		'<span class="vzp-facetwp-listing__loader-label">' +
			LOADING_LABEL +
		'</span>';

	// insertBefore requires a direct child; block-editor pages may wrap the template.
	const template = root.querySelector(':scope > .facetwp-template');
	if (template) {
		root.insertBefore(loader, template);
	} else {
		root.appendChild(loader);
	}

	return loader;
}

function setTemplatesLoading(on) {
	document.querySelectorAll('.facetwp-template').forEach((template) => {
		template.classList.toggle('is-loading', on);
	});
}

function clearEnterAnimation(root) {
	const template = root.querySelector('.facetwp-template');
	if (!template) {
		return;
	}

	template.classList.remove(ENTER_CLASS);
	template.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
		item.style.removeProperty('--vzp-listing-enter-delay');
	});
}

function animateResultsIn(root) {
	if (prefersReducedMotion()) {
		return;
	}

	const template = root.querySelector('.facetwp-template');
	if (!template) {
		return;
	}

	clearEnterAnimation(root);

	const items = template.querySelectorAll(ITEM_SELECTOR);
	if (!items.length) {
		return;
	}

	template.classList.add(ENTER_CLASS);
	items.forEach((item, index) => {
		const delay = Math.min(index * STAGGER_MS, STAGGER_MAX_MS);
		item.style.setProperty('--vzp-listing-enter-delay', `${delay}ms`);
	});

	if (enterCleanupTimer) {
		clearTimeout(enterCleanupTimer);
	}

	enterCleanupTimer = setTimeout(() => {
		clearEnterAnimation(root);
		enterCleanupTimer = null;
	}, STAGGER_MAX_MS + 520);
}

function showLoader(root) {
	const loader = ensureLoader(root);
	loader.classList.remove(LOADER_OUT_CLASS);
	clearEnterAnimation(root);
	root.classList.add(LOADING_CLASS);
	root.setAttribute('aria-busy', 'true');
	loader.hidden = false;
}

function hideLoader(root) {
	const loader = root.querySelector(LOADER_SELECTOR);
	if (!loader || loader.hidden) {
		root.classList.remove(LOADING_CLASS);
		root.setAttribute('aria-busy', 'false');
		animateResultsIn(root);
		return;
	}

	if (prefersReducedMotion()) {
		loader.hidden = true;
		loader.classList.remove(LOADER_OUT_CLASS);
		root.classList.remove(LOADING_CLASS);
		root.setAttribute('aria-busy', 'false');
		animateResultsIn(root);
		return;
	}

	loader.classList.add(LOADER_OUT_CLASS);
	root.classList.remove(LOADING_CLASS);
	root.setAttribute('aria-busy', 'false');

	setTimeout(() => {
		loader.classList.remove(LOADER_OUT_CLASS);
		loader.hidden = true;
		animateResultsIn(root);
	}, LOADER_OUT_MS);
}

function getListingRoots() {
	return document.querySelectorAll(ROOT_SELECTOR);
}

function initListingRoots() {
	getListingRoots().forEach((root) => {
		ensureLoader(root);
	});
}

function setAllLoading(on) {
	setTemplatesLoading(on);

	getListingRoots().forEach((root) => {
		if (on) {
			showLoader(root);
		} else {
			hideLoader(root);
		}
	});
}

function onRefresh() {
	if (!shouldShowLoader()) {
		return;
	}

	if (hideTimer) {
		clearTimeout(hideTimer);
		hideTimer = null;
	}

	loadingStartedAt = Date.now();
	initListingRoots();
	setAllLoading(true);
}

function onLoaded() {
	if (!shouldShowLoader()) {
		return;
	}

	const elapsed = Date.now() - loadingStartedAt;
	const delay = Math.max(0, MIN_LOADING_MS - elapsed);

	if (hideTimer) {
		clearTimeout(hideTimer);
	}

	hideTimer = setTimeout(() => {
		setAllLoading(false);
		hideTimer = null;
	}, delay);
}

export function initFacetwpListingLoader() {
	if (document.querySelector(ROOT_SELECTOR)) {
		initListingRoots();
	}

	document.addEventListener('facetwp-refresh', onRefresh);
	document.addEventListener('facetwp-loaded', onLoaded);
}
